import { Button as AntdButton, Checkbox, DatePicker, InputNumber, Modal, Select, Spin, message } from "antd"
import dayjs from "dayjs"
import { useEffect, useRef, useState } from "react"
import { Button as BootstrapButton, Col, Form, Row } from "react-bootstrap"
import { useHistory } from "react-router-dom"
import * as com from "../Common"
import { Tips } from "../Common/Tooltip"
import CheckGroup from "../Common/components/CheckGroup"
import { DollarInputV2 } from "./DollarInputV2"
import "../Application/SSN.css"
import "../Application/ApplicationInterview.css"
import "./styles.css"
import { pullParticipantCreditV2 } from "./api/borrowerSideV2Client"
import { listPendingCreditPullPaymentRequestsV2 } from "./api/creditPullPaymentV2Client"
import { listCreditPullProfileGaps, showCreditPullProfileGapWarning } from "./creditPullProfileGaps"
import { canAuthorizeAndSubmitV2 } from "./resumePath"
import { track } from "../statsig"
import { STATSIG } from "../statsig/events"

const emptyDeclarations = {
    judgements: null,
    bancrupt: null,
    chapter7: false,
    chapter11: false,
    chapter12: false,
    chapter13: false,
    propertyforeclosure: null,
    lawsuit: null,
    familyrelationship: null,
    conveyedtitle: null,
    delinquentondebt: null,
    applyingonanother: null,
    applyingnewcredit: null,
    cleanenergylien: null,
    loanforeclosure: null,
    downpaymentborrowed: null,
    moneyborrowed: "",
    comakeronnote: null,
    occupyasprimary: null,
    ownershipinterest: null,
    servearmedforces: null,
    militaryservice: {
        currentlyserving: false,
        currentlyretired: false,
        nonactive: false,
        survivingspouse: false,
        projectedexpirationdate: "",
    },
    propertyowned: "principal",
    titleheld: "yourself",
    loansinforebearance: null,
    equalopportunity: {
        ethnicity: "",
        hispanic: {
            mexican: false,
            puertorican: false,
            cuban: false,
            other: false,
            otherdesctiption: "",
        },
        sex: "",
        race: {
            notfurnished: false,
            asian: false,
            pacific: false,
            black: false,
            americanindian: false,
            white: false,
            indiantribe: "",
            asiandetails: {
                asianindian: false,
                chinese: false,
                filipino: false,
                japanese: false,
                korean: false,
                vietnamese: false,
                otherasian: false,
                otherdesctiption: "",
            },
            pacificdetails: {
                hawaian: false,
                guamanian: false,
                samoan: false,
                other: false,
                otherdetails: "",
            },
        },
    },
}

const STEP_TITLE = {
    propertymoney: "Property & money",
    finances: "Finances",
    creditcheck: "Credit check",
}

const normalizeDeclarations = (declarations = {}) => ({
    ...emptyDeclarations,
    ...declarations,
    moneyborrowed: declarations.moneyborrowed == null || declarations.moneyborrowed === ""
        ? ""
        : `${declarations.moneyborrowed}`,
    militaryservice: {
        ...emptyDeclarations.militaryservice,
        ...(declarations.militaryservice || {}),
    },
    equalopportunity: {
        ...emptyDeclarations.equalopportunity,
        ...(declarations.equalopportunity || {}),
        hispanic: {
            ...emptyDeclarations.equalopportunity.hispanic,
            ...(declarations.equalopportunity?.hispanic || {}),
        },
        race: {
            ...emptyDeclarations.equalopportunity.race,
            ...(declarations.equalopportunity?.race || {}),
            asiandetails: {
                ...emptyDeclarations.equalopportunity.race.asiandetails,
                ...(declarations.equalopportunity?.race?.asiandetails || {}),
            },
            pacificdetails: {
                ...emptyDeclarations.equalopportunity.race.pacificdetails,
                ...(declarations.equalopportunity?.race?.pacificdetails || {}),
            },
        },
    },
    propertyowned: declarations.propertyowned || "principal",
    titleheld: declarations.titleheld || "yourself",
})

const purposeValue = (application) => application?.property?.purpose || ""

const yesNoOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
]

// Legacy declarations display every value other than the explicit "yes" as No.
const yesNoValue = (value) => (value === "yes" ? "yes" : "no")

const hasAmount = (value) => {
    if (value === "" || value == null) return false
    const parsed = Number(value)
    return !Number.isNaN(parsed)
}

const FieldError = ({ message }) => (
    message ? <div className="warning-msg">{message}</div> : null
)

const scrollToField = (fieldKey) => {
    if (!fieldKey || typeof document === "undefined") return
    const el = document.querySelector(`[data-field="${fieldKey}"]`)
    if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
    }
}

// Yes/No defaults to No in the UI; only conditional amount needs Next validation.
const validatePropertyMoney = (declarations) => {
    const errors = {}
    if (declarations.downpaymentborrowed === "yes" && !hasAmount(declarations.moneyborrowed)) {
        errors.moneyborrowed = "Please provide the amount of this money."
    }
    return { errors, firstErrorKey: errors.moneyborrowed ? "moneyborrowed" : null }
}

const FINANCES_FIELD_ORDER = [
    "ethnicity",
    "hispanic",
    "hispanicOther",
    "sex",
    "race",
    "pacific",
    "pacificOther",
    "indiantribe",
    "asian",
    "asianOther",
]

// Mirror legacy HTML5 required rules on equal-opportunity fields (reportValidity).
const validateFinances = (declarations) => {
    const errors = {}
    const { ethnicity, hispanic, race, sex } = declarations.equalopportunity || emptyDeclarations.equalopportunity

    if (!ethnicity) {
        errors.ethnicity = "Please select your ethnicity."
    } else if (ethnicity === "hispanic") {
        if (!hispanic.mexican && !hispanic.puertorican && !hispanic.cuban && !hispanic.other) {
            errors.hispanic = "Please select at least one Hispanic or Latino origin."
        }
        if (hispanic.other && !`${hispanic.otherdesctiption || ""}`.trim()) {
            errors.hispanicOther = "Please describe your Hispanic or Latino origin."
        }
    }

    if (!sex) {
        errors.sex = "Please select your sex."
    }

    const hasRace = race.notfurnished || race.asian || race.pacific || race.black || race.americanindian || race.white
    if (!hasRace) {
        errors.race = "Please select at least one race."
    } else if (!race.notfurnished) {
        if (race.pacific) {
            const pd = race.pacificdetails || {}
            if (!pd.hawaian && !pd.samoan && !pd.guamanian && !pd.other) {
                errors.pacific = "Please select at least one Pacific Islander origin."
            }
            if (pd.other && !`${pd.otherdetails || ""}`.trim()) {
                errors.pacificOther = "Please describe your Pacific Islander origin."
            }
        }
        if (race.americanindian && !`${race.indiantribe || ""}`.trim()) {
            errors.indiantribe = "Please provide the name of enrolled or principal tribe."
        }
        if (race.asian) {
            const ad = race.asiandetails || {}
            if (!ad.otherasian && !ad.asianindian && !ad.chinese && !ad.korean && !ad.vietnamese && !ad.filipino && !ad.japanese) {
                errors.asian = "Please select at least one Asian origin."
            }
            if (ad.otherasian && !`${ad.otherdesctiption || ""}`.trim()) {
                errors.asianOther = "Please describe your Asian origin."
            }
        }
    }

    const firstErrorKey = FINANCES_FIELD_ORDER.find((key) => !!errors[key]) || null
    return { errors, firstErrorKey }
}

const CREDIT_CHECK_FIELD_ORDER = [
    "socialSecurity",
    "creditconsented",
    "estimatedFico",
    "estimatedMonthlyDebt",
]

// Same gate as the former Next-button disable; errors only after Next click.
const validateCreditCheck = (creditCheck) => {
    const errors = {}
    const ssn = `${creditCheck.socialSecurity || ""}`
    const ssnValid = ssn.length === 11
    const manualCredit = creditCheck.pullCredit === "false" || creditCheck.showFico
    const ficoValid = Number(creditCheck.estimatedFico) >= 500

    if (!ssnValid) {
        errors.socialSecurity = ssn ? "SSN must be 9 digits" : "Please enter a valid SSN"
    }
    if (!creditCheck.creditconsented) {
        errors.creditconsented = "Please consent to the credit check"
    }
    if (manualCredit && !ficoValid) {
        errors.estimatedFico = "Please enter your FICO score"
    }
    // Display-only (legacy did not block Next on empty debt); show with other errors after attempt.
    if (manualCredit && !creditCheck.estimatedMonthlyDebt) {
        errors.estimatedMonthlyDebt = "Please enter your monthly debt payment"
    }

    const blockers = ["socialSecurity", "creditconsented", "estimatedFico"]
    const firstErrorKey = CREDIT_CHECK_FIELD_ORDER.find((key) => !!errors[key] && blockers.includes(key))
        || null
    return { errors, firstErrorKey }
}

const formatSSN = (value) => {
    const ssn = `${value || ""}`.replace(/[^\d]/g, "")
    if (ssn.length < 4) {
        return ssn
    }
    if (ssn.length < 6) {
        return `${ssn.slice(0, 3)}-${ssn.slice(3)}`
    }
    return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5, 9)}`
}

const fullName = (participant) => (
    [participant?.firstName, participant?.middleName, participant?.lastName]
        .filter(Boolean)
        .join(" ")
)

const formatAddress = (address = {}) => (
    [address.street, address.unit, address.city, address.state, address.zip]
        .filter(Boolean)
        .join(", ")
)

const listSoftPullProfileGaps = (application, participant, socialSecurity) => {
    const primaryPartyId = application?.primaryPartyId || application?.participantOrder?.[0] || ""
    const cohabitingPartyId = `${application?.cohabitingPartyId || ""}`.trim()
    const currentId = participant?.partyId || ""
    const isSpousePair = !!(cohabitingPartyId && (currentId === primaryPartyId || currentId === cohabitingPartyId))
    const subjectIds = isSpousePair ? [primaryPartyId, cohabitingPartyId] : [currentId]
    const subjects = subjectIds.map((partyId) => {
        const person = partyId === currentId
            ? participant
            : application?.participantsById?.[partyId]
        if (!person && partyId !== currentId) return null
        const subject = person || participant
        if (partyId === currentId) {
            return { ...subject, socialSecurity }
        }
        return subject
    }).filter(Boolean)
    return listCreditPullProfileGaps(subjects)
}

const resolveCreditScore = (report, scoreRole) => {
    const scoreModels = Array.isArray(report?.scoreModels) ? report.scoreModels : []
    const matched = scoreRole
        ? scoreModels.filter((item) => item.role === scoreRole)
        : scoreModels
    const scores = matched
        .map((item) => Number(item.scoreValue))
        .filter((score) => !Number.isNaN(score))
    if (scores.length === 0) {
        return null
    }
    return Math.max(...scores)
}

const isMortgageLoan = (loan = {}) => {
    const loanType = `${loan.loanType || loan.creditLoanType || loan.creditloantype || loan.accountType || ""}`.toLowerCase()
    const accountType = `${loan.accountType || loan.creditliabilityaccounttype || ""}`.toLowerCase()
    return ["mortgage", "realestate", "homeequity", "heloc"].some((value) => loanType.includes(value) || accountType.includes(value))
}

const toCreditFieldString = (value) => {
    if (value == null || value === "") {
        return ""
    }
    return `${value}`
}

const normalizeCreditLiability = (loan = {}, index, participant, application, pullMeta = {}) => {
    const accountType = loan.accountType || loan.creditliabilityaccounttype || ""
    const loanType = loan.loanType || loan.creditLoanType || loan.creditloantype || accountType || "Installment"
    let monthlyPayment = loan.monthlyPaymentAmount ?? loan.monthlypayment ?? loan.monthlyPayment ?? ""
    const remainingBalance = loan.unpaidBalanceAmount ?? loan.remainingbalance ?? loan.remainingBalance ?? ""
    const isLease = accountType === "AutoLease" || accountType === "Lease"
    const isInstallment = accountType === "Installment"
    let monthlyDefinedByCustomer = false
    if (`${loanType}`.toLowerCase().includes("education")) {
        if (monthlyPayment === "" || monthlyPayment == null) monthlyPayment = 0
    } else if ((isLease || isInstallment) && (monthlyPayment === 0 || monthlyPayment === "" || monthlyPayment == null)) {
        monthlyDefinedByCustomer = true
    }
    const subjectPartyIds = Array.isArray(pullMeta.subjectPartyIds) ? pullMeta.subjectPartyIds.filter(Boolean) : []
    const isJointPull = pullMeta.pullMode === "joint" && subjectPartyIds.length > 1
    const ownership = isJointPull
        ? "Joint"
        : (participant?.isPrimary ? "Borrower" : "CoBorrower")
    const ownerPartyIds = isJointPull
        ? subjectPartyIds
        : [participant?.partyId].filter(Boolean)
    const normalized = {
        key: loan.key || loan.sourceKey || `credit-${Date.now()}-${index}`,
        creditLoanType: loanType,
        openedDate: toCreditFieldString(loan.openedDate || loan.openeddate || ""),
        name: loan.name || loan.creditorName || loan.creditorname || "",
        address: loan.address || "",
        accountNumber: toCreditFieldString(loan.accountNumber || loan.accountnumber || ""),
        monthlyPayment: toCreditFieldString(monthlyPayment),
        remainingBalance: toCreditFieldString(remainingBalance),
        highBalance: toCreditFieldString(loan.highBalanceAmount ?? loan.highbalance ?? loan.highBalance ?? ""),
        monthsLeftToPay: toCreditFieldString(loan.monthsRemainingCount ?? loan.monthsLeftToPay ?? loan.monthslefttopay ?? ""),
        ownership,
        ownerPartyIds,
        satisfiedUpon: loan.satisfiedUpon || loan.satisfiedupon || "not",
        paidOffAtClosing: toCreditFieldString(loan.paidOffAtClosing || loan.paidoffatclosing || ""),
        rate: toCreditFieldString(loan.rate || ""),
        correction: loan.correction || "none",
        explanation: loan.explanation || "",
        propertyAddress: loan.propertyAddress || loan.propertyaddress || "",
        creditLimit: toCreditFieldString(loan.creditLimit || loan.creditlimit || ""),
        monthlyDefinedByCustomer,
    }

    const streetMatch = (left = "", right = "") => `${left}`.split(",")[0].trim().toLowerCase() === `${right}`.trim().toLowerCase()
    const presentStreet = participant?.presentAddress?.street || participant?.presentAddress?.address || ""
    if (
        normalized.address
        && streetMatch(normalized.address, presentStreet)
        && `${loan.accountStatus || ""}`.toLowerCase() === "open"
        && isMortgageLoan(loan)
    ) {
        normalized.forPrimaryResidence = true
    }
    return normalized
}

const buildEstimatedLiability = (participant, monthlyDebt) => ({
    key: "estimated-monthly-debt",
    // 2c Category: product wants estimated monthly debt stored as Installment.
    creditLoanType: "Installment",
    openedDate: "01/01/2023",
    name: "Estimated",
    address: "",
    accountNumber: "",
    monthlyPayment: monthlyDebt == null || monthlyDebt === "" ? "" : `${monthlyDebt}`,
    remainingBalance: "",
    highBalance: "",
    monthsLeftToPay: "",
    ownership: participant?.isPrimary ? "Borrower" : "CoBorrower",
    satisfiedUpon: "not",
    paidOffAtClosing: "",
    rate: "",
    correction: "none",
    explanation: "",
    propertyAddress: "",
    creditLimit: "",
    monthlyDefinedByCustomer: true,
})

// Credit-check owns 2c loans (and estimated monthly debt). It must not rewrite
// 2d additional expenses / alimony — those live on the Liabilities step and are
// stored as income.alimony. Sending additionalExpenses: [] used to wipe them.
const liabilitiesWithoutAdditionalExpenses = (liabilities) => {
    if (!liabilities || typeof liabilities !== "object") {
        return liabilities
    }
    const { additionalExpenses: _omit, ...rest } = liabilities
    return rest
}

const withEstimatedLiabilityIfManual = (creditCheck, liabilities, participant) => {
    const manualCredit = creditCheck?.pullCredit === "false" || creditCheck?.showFico
    if (!manualCredit) {
        return creditCheck
    }
    const estimated = buildEstimatedLiability(participant, creditCheck.estimatedMonthlyDebt)
    return {
        ...creditCheck,
        liabilities: {
            ...liabilitiesWithoutAdditionalExpenses(liabilities || {}),
            loans: [estimated],
            newMortgages: liabilities?.newMortgages || [],
        },
    }
}

const YesNoQuestion = ({ label, value, onChange, error, fieldKey }) => (
    <Row>
        <Col className="mt-4 px-0" md={12} xs={12} data-field={fieldKey}>
            <Form.Group className="inputSection">
                <Form.Label>{label}</Form.Label>
                <Row>
                    <Col className="pl-0" md={6} xs={12}>
                        <Form.Group className="inputSection">
                            <CheckGroup
                                className="property-v2-yesno"
                                data={yesNoOptions}
                                value={yesNoValue(value)}
                                onChangeSelect={onChange}
                                type="radio"
                                direction="horizontal"
                            />
                            <FieldError message={error} />
                        </Form.Group>
                    </Col>
                </Row>
            </Form.Group>
        </Col>
    </Row>
)

const DeclarationStepV2 = ({ step, application, participant, declarations: initialDeclarations, liabilities, saving, onSave, onBack }) => {
    const history = useHistory()
    const formRef = useRef(null)
    const [declarations, setDeclarations] = useState(emptyDeclarations)
    const [fieldErrors, setFieldErrors] = useState({})
    const [pendingHardPayment, setPendingHardPayment] = useState(null)
    const [creditCheck, setCreditCheck] = useState({
        automatedCreditPull: false,
        loading: false,
        socialSecurity: "",
        estimatedFico: 0,
        estimatedMonthlyDebt: "",
        creditconsented: false,
        pullCredit: "false",
        errorMessages: [],
        showCreditError: false,
        showFico: false,
        // Mirrors legacy SSN.js showCheckCredit: Soft/Manual radios only while choosing/redoing.
        showCheckCredit: true,
        checkingCredit: false,
        softPullSucceeded: false,
        ssnChangedAfterPull: false,
        showContactModal: false,
    })
    const [showSubmitModal, setShowSubmitModal] = useState(false)
    const [pendingSubmit, setPendingSubmit] = useState(null)
    const isMobileSubmit = typeof window !== "undefined" && window.innerWidth < 576
    const canSubmitApplication = step === "creditcheck"
        && canAuthorizeAndSubmitV2(application, participant?.partyId)

    const confirmSubmitApplication = async () => {
        if (!pendingSubmit) {
            return
        }
        const payload = pendingSubmit
        setShowSubmitModal(false)
        setPendingSubmit(null)
        await onSave(payload)
    }

    useEffect(() => {
        setDeclarations(normalizeDeclarations(initialDeclarations))
        setFieldErrors({})
    }, [initialDeclarations])

    // Pending borrower-paid hard-pull link for this loan (soft check is unaffected).
    useEffect(() => {
        if (step !== "creditcheck") {
            setPendingHardPayment(null)
            return
        }
        const loanId = application?.loanId
        if (!loanId) {
            setPendingHardPayment(null)
            return
        }
        let cancelled = false
        listPendingCreditPullPaymentRequestsV2()
            .then((body) => {
                if (cancelled) return
                const match = (body?.Requests || []).find((item) => item?.loanId === loanId)
                setPendingHardPayment(match || null)
            })
            .catch(() => {
                if (!cancelled) setPendingHardPayment(null)
            })
        return () => { cancelled = true }
    }, [step, application?.loanId])

    useEffect(() => {
        const loans = liabilities?.loans || []
        const hasPulledCredit = Number(participant?.estimatedFico || 0) !== 0
            && loans.length > 0
            && !(loans.length === 1 && loans[0]?.name === "Estimated")
        const hasSavedCreditError = application?.creditWarnings !== null
            && application?.creditWarnings !== undefined
            && !hasPulledCredit
        const savedWarnings = Array.isArray(application?.creditWarnings) && hasSavedCreditError
            ? application.creditWarnings
            : []
        // Align with legacy initStatus(): success → hide radios; fail → show FICO only.
        // Keep in-session softPullSucceeded when liabilities are not yet imported (e.g. joint spouse).
        setCreditCheck((prev) => {
            const softPullSucceeded = hasPulledCredit || (prev.softPullSucceeded && !hasSavedCreditError)
            return {
                ...prev,
                loading: false,
                socialSecurity: prev.ssnChangedAfterPull
                    ? prev.socialSecurity
                    : formatSSN(participant?.socialSecurity || ""),
                estimatedFico: participant?.estimatedFico || prev.estimatedFico || 0,
                estimatedMonthlyDebt: participant?.estimatedMonthlyDebt || "",
                creditconsented: Boolean(participant?.creditconsented),
                pullCredit: softPullSucceeded && !hasSavedCreditError
                    ? "true"
                    : (hasSavedCreditError ? "false" : prev.pullCredit),
                errorMessages: savedWarnings.length > 0 ? savedWarnings : prev.errorMessages,
                // While redoing after SSN edit, keep Soft/Manual open even if prior fail warnings remain.
                showCreditError: hasSavedCreditError || (prev.showCreditError && prev.ssnChangedAfterPull),
                showFico: (hasSavedCreditError && !prev.ssnChangedAfterPull)
                    ? true
                    : (prev.ssnChangedAfterPull ? prev.showFico : false),
                showCheckCredit: (hasSavedCreditError && !prev.ssnChangedAfterPull)
                    ? false
                    : (prev.ssnChangedAfterPull ? true : !softPullSucceeded),
                checkingCredit: false,
                softPullSucceeded,
                showContactModal: false,
            }
        })
    }, [application, participant, liabilities])

    useEffect(() => {
        if (step !== "creditcheck") {
            return
        }

        setCreditCheck((prev) => ({ ...prev, loading: true }))
        const token = window.sessionStorage.getItem("ZeitroA")
        fetch("/borrower/checkautomatedcreditpull", {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                Cache: "no-cache",
            },
        })
            .then((response) => {
                if (response.status !== 200) {
                    setCreditCheck((prev) => ({ ...prev, automatedCreditPull: false, pullCredit: "false", loading: false }))
                    return null
                }
                return response.json()
            })
            .then((body) => {
                if (!body) {
                    return
                }
                const useAutomated = body.UseAutomatedCreditPull === true
                    || body.UseAutomatedCreditPull === "true"
                setCreditCheck((prev) => ({
                    ...prev,
                    automatedCreditPull: useAutomated,
                    // Don't clobber post-pull / fail mode selection.
                    pullCredit: useAutomated
                        ? (prev.showCreditError || prev.showFico ? "false" : "true")
                        : "false",
                    loading: false,
                }))
            })
            .catch(() => {
                setCreditCheck((prev) => ({ ...prev, automatedCreditPull: false, pullCredit: "false", loading: false }))
            })
    }, [step])

    const clearFieldError = (key) => {
        setFieldErrors((prev) => {
            if (!prev[key]) return prev
            const next = { ...prev }
            delete next[key]
            return next
        })
    }

    const setField = (key, value) => {
        clearFieldError(key)
        setDeclarations((prev) => ({ ...prev, [key]: value }))
    }

    const setMilitary = (key, value) => {
        setDeclarations((prev) => ({
            ...prev,
            militaryservice: {
                ...prev.militaryservice,
                [key]: value,
            },
        }))
    }

    const setEqualOpportunity = (key, value) => {
        if (key === "ethnicity") {
            clearFieldError("ethnicity")
            clearFieldError("hispanic")
            clearFieldError("hispanicOther")
        }
        if (key === "sex") clearFieldError("sex")
        setDeclarations((prev) => ({
            ...prev,
            equalopportunity: {
                ...prev.equalopportunity,
                [key]: value,
            },
        }))
    }

    const setEqualOpportunitySub = (section, key, value) => {
        if (section === "hispanic") {
            clearFieldError("hispanic")
            if (key === "otherdesctiption" || key === "other") clearFieldError("hispanicOther")
        }
        if (section === "race") {
            clearFieldError("race")
            if (key === "indiantribe" || key === "americanindian") clearFieldError("indiantribe")
            if (key === "pacific") {
                clearFieldError("pacific")
                clearFieldError("pacificOther")
            }
            if (key === "asian") {
                clearFieldError("asian")
                clearFieldError("asianOther")
            }
            if (key === "notfurnished") {
                clearFieldError("pacific")
                clearFieldError("pacificOther")
                clearFieldError("indiantribe")
                clearFieldError("asian")
                clearFieldError("asianOther")
            }
        }
        if (section === "race|pacificdetails") {
            clearFieldError("pacific")
            if (key === "otherdetails" || key === "other") clearFieldError("pacificOther")
        }
        if (section === "race|asiandetails") {
            clearFieldError("asian")
            if (key === "otherdesctiption" || key === "otherasian") clearFieldError("asianOther")
        }
        const parts = section.split("|")
        setDeclarations((prev) => {
            if (parts.length === 1) {
                return {
                    ...prev,
                    equalopportunity: {
                        ...prev.equalopportunity,
                        [section]: {
                            ...prev.equalopportunity[section],
                            [key]: value,
                        },
                    },
                }
            }

            const [parent, child] = parts
            return {
                ...prev,
                equalopportunity: {
                    ...prev.equalopportunity,
                    [parent]: {
                        ...prev.equalopportunity[parent],
                        [child]: {
                            ...prev.equalopportunity[parent][child],
                            [key]: value,
                        },
                    },
                },
            }
        })
    }

    const setCreditField = (key, value) => {
        if (key === "socialSecurity" || key === "creditconsented" || key === "estimatedFico" || key === "estimatedMonthlyDebt") {
            clearFieldError(key)
        }
        setCreditCheck((prev) => ({ ...prev, [key]: value }))
    }

    const requestSoftCredit = async () => {
        const profileGaps = listSoftPullProfileGaps(application, participant, creditCheck.socialSecurity)
        if (profileGaps.length) {
            showCreditPullProfileGapWarning(
                profileGaps,
                "Fill in SSN and Current residential address before checking credit.",
            )
            return
        }
        setCreditCheck((prev) => ({ ...prev, checkingCredit: true, errorMessages: [], showCreditError: false }))
        try {
            if (!participant?.partyId) {
                throw new Error("Unable to locate the active participant for credit check.")
            }

            const pullBody = await pullParticipantCreditV2(participant.partyId, {
                socialSecurity: creditCheck.socialSecurity,
                creditconsented: creditCheck.creditconsented,
            })
            const report = pullBody.report
            if (!report) {
                throw new Error(pullBody.Message || "The credit report did not return any data.")
            }

            const pullMeta = {
                pullMode: pullBody.pullMode || "individual",
                subjectPartyIds: pullBody.subjectPartyIds || [],
                importLiabilities: pullBody.importLiabilities === true,
            }
            const estimatedFico = resolveCreditScore(report, pullBody.scoreRole || "")
            if (estimatedFico == null) {
                track(STATSIG.LOAN.CREDIT_PULL, "success", {
                    type: "soft",
                    result: "success",
                    party_id: participant.partyId,
                    has_score: "false",
                })
                setCreditCheck((prev) => ({
                    ...prev,
                    checkingCredit: false,
                    showFico: true,
                    showCheckCredit: false,
                    pullCredit: "false",
                    softPullSucceeded: false,
                    ssnChangedAfterPull: false,
                    showCreditError: true,
                    errorMessages: ["No credit score was returned for this applicant. Please enter your estimated FICO score."],
                }))
                await onSave({
                    step,
                    declarations,
                    creditCheck: {
                        socialSecurity: creditCheck.socialSecurity,
                        creditconsented: creditCheck.creditconsented,
                        estimatedFico: creditCheck.estimatedFico,
                        estimatedMonthlyDebt: creditCheck.estimatedMonthlyDebt,
                        creditWarnings: ["No credit score was returned for this applicant."],
                        complete: false,
                    },
                    stayOnStep: true,
                })
                return
            }

            const reportLiabilities = Array.isArray(report.liabilities) ? report.liabilities : []
            const nextLoans = pullMeta.importLiabilities
                ? reportLiabilities
                    .filter((loan) => {
                        const balance = Number(loan.unpaidBalanceAmount ?? loan.remainingBalance ?? loan.remainingbalance ?? 0)
                        return Number.isNaN(balance) || balance !== 0
                    })
                    .map((loan, index) => normalizeCreditLiability(loan, index, participant, application, pullMeta))
                : null

            const nextLiabilities = nextLoans
                ? {
                    ...liabilitiesWithoutAdditionalExpenses(liabilities || {}),
                    loans: nextLoans.length > 0 ? nextLoans : (liabilities?.loans || []),
                    newMortgages: liabilities?.newMortgages || [],
                }
                : null

            // Align with legacy initStatus success: hide Soft/Manual radios, show congratulations only.
            track(STATSIG.LOAN.CREDIT_PULL, "success", {
                type: "soft",
                result: "success",
                party_id: participant.partyId,
                has_score: "true",
            })
            setCreditCheck((prev) => ({
                ...prev,
                checkingCredit: false,
                estimatedFico,
                showCreditError: false,
                showFico: false,
                showCheckCredit: false,
                softPullSucceeded: true,
                ssnChangedAfterPull: false,
                errorMessages: [],
            }))
            await onSave({
                step,
                declarations,
                creditCheck: {
                    socialSecurity: creditCheck.socialSecurity,
                    creditconsented: creditCheck.creditconsented,
                    estimatedMonthlyDebt: creditCheck.estimatedMonthlyDebt,
                    estimatedFico,
                    ...(nextLiabilities ? { liabilities: nextLiabilities } : {}),
                    creditWarnings: null,
                    complete: false,
                },
                stayOnStep: true,
            })
        } catch (err) {
            track(STATSIG.LOAN.CREDIT_PULL, "fail", {
                type: "soft",
                result: "fail",
                party_id: participant?.partyId || "",
                has_score: "false",
            })
            setCreditCheck((prev) => ({
                ...prev,
                checkingCredit: false,
                showFico: true,
                showCheckCredit: false,
                pullCredit: "false",
                softPullSucceeded: false,
                ssnChangedAfterPull: false,
                errorMessages: [err.message],
                showCreditError: true,
            }))
            try {
                await onSave({
                    step,
                    declarations,
                    creditCheck: {
                        socialSecurity: creditCheck.socialSecurity,
                        creditconsented: creditCheck.creditconsented,
                        estimatedFico: creditCheck.estimatedFico,
                        estimatedMonthlyDebt: creditCheck.estimatedMonthlyDebt,
                        creditWarnings: [err.message],
                        complete: false,
                    },
                    stayOnStep: true,
                })
            } catch (_) { /* keep local error UI even if warning persistence fails */ }
            message.error(err.message)
        }
    }

    const contactLoanOfficer = () => {
        const token = window.sessionStorage.getItem("ZeitroA")
        // Borrower-side token only — do not send X-Borrower (that header means LO impersonation).
        fetch("/borrower/contactloaboutcreditpull", {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                Cache: "no-cache",
            },
        }).catch(() => {})
        setCreditCheck((prev) => ({ ...prev, showContactModal: true }))
    }

    const renderCreditCheck = () => {
        const name = fullName(participant)
        const suffix = participant?.suffix || ""
        const label = name
            ? <>*What&apos;s <span style={{ color: "#325CEB" }}>{name}</span>&apos;s Social Security Number?</>
            : "*What's your Social Security Number?"
        const enterSSN = () => (
            <div>
                <Form.Label>{label}</Form.Label>
                <div data-field="socialSecurity">
                    <Form.Control
                        isInvalid={!!fieldErrors.socialSecurity}
                        size="md"
                        type="text"
                        value={creditCheck.socialSecurity}
                        placeholder=""
                        onChange={(event) => {
                            const nextSSN = formatSSN(event.target.value)
                            clearFieldError("socialSecurity")
                            setCreditCheck((prev) => ({
                                ...prev,
                                socialSecurity: nextSSN,
                                // Legacy: editing SSN re-opens Soft/Manual; hide success banner only
                                // (showStatusInfo←false). Fail banner can remain while redoing.
                                showCheckCredit: true,
                                showFico: false,
                                ssnChangedAfterPull:
                                    prev.softPullSucceeded
                                    || prev.showCreditError
                                    || prev.ssnChangedAfterPull,
                            }))
                        }}
                    />
                    <FieldError message={fieldErrors.socialSecurity} />
                </div>
                <Form.Group className="mt-3" data-field="creditconsented">
                    <Form.Check
                        type="checkbox"
                        className="credit-checkbox-default"
                        checked={creditCheck.creditconsented}
                        name="confirm"
                        label="*I/We consent to a credit check and to the verification of employment, assets, credit, mortgage, and rental history as necessary to process this mortgage application."
                        onChange={(event) => setCreditField("creditconsented", event.target.checked)}
                    />
                    <FieldError message={fieldErrors.creditconsented} />
                </Form.Group>
            </div>
        )
        const displayEstimatedFicoAndDebt = () => (
            <div className="mt-4 fico-info-flex">
                <Form.Group className="inputSection" data-field="estimatedFico">
                    <Form.Label>
                        <div className="creditCheck-label">
                            *What is your estimated FICO score?
                        </div>
                    </Form.Label>
                    <InputNumber
                        style={{ width: "100%" }}
                        size="large"
                        status={fieldErrors.estimatedFico ? "error" : undefined}
                        value={creditCheck.estimatedFico || null}
                        min={500}
                        max={1000}
                        precision={0}
                        onChange={(value) => setCreditField("estimatedFico", value || 0)}
                    />
                    <FieldError message={fieldErrors.estimatedFico} />
                </Form.Group>
                <Form.Group className="inputSection" data-field="estimatedMonthlyDebt">
                    <Form.Label>
                        <div className="creditCheck-label">
                            What is your estimated monthly debt payment?
                        </div>
                    </Form.Label>
                    <DollarInputV2
                        style={{ width: "100%" }}
                        status={fieldErrors.estimatedMonthlyDebt ? "error" : undefined}
                        value={creditCheck.estimatedMonthlyDebt || null}
                        onChange={(value) => setCreditField("estimatedMonthlyDebt", value == null ? "" : `${value}`)}
                    />
                    <FieldError message={fieldErrors.estimatedMonthlyDebt} />
                </Form.Group>
            </div>
        )

        const goToPendingHardPayment = () => {
            if (!pendingHardPayment?.id) return
            const cid = (com.getCustomerId() || "").trim()
            const q = cid ? `?customerid=${encodeURIComponent(cid)}` : ""
            track(STATSIG.PAYMENT.OPEN, "credit_pull", {
                type: "credit_pull",
                source: "creditcheck",
                request_id: pendingHardPayment.id,
                loan_id: application?.loanId || "",
            })
            history.push(`/application/credit-report-payment/${pendingHardPayment.id}${q}`)
        }

        // Only show when LO already sent a hard-pull payment link (there is a fee to pay).
        // Do not show the generic "no fee / link later" tip when nothing is pending.
        const softFeeTip = pendingHardPayment?.id ? (
            <div className="credit-check-payment-tip">
                <div className="credit-check-payment-tip-icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <div className="credit-check-payment-tip-text">
                    No fee for this step — your lender covers the soft credit check. Your loan officer sent a{" "}
                    <button type="button" className="credit-check-payment-tip-link" onClick={goToPendingHardPayment}>
                        {`secure payment link ($${Number(pendingHardPayment.amount || 0).toFixed(2)})`}
                    </button>
                    {" "}for the hard credit report. No inquiry occurs until you pay.
                </div>
            </div>
        ) : null

        const manualScoreTip = null

        const creditCheckTips = (manualScoreTip || softFeeTip) ? (
            <div className="credit-check-tips">
                {manualScoreTip}
                {softFeeTip}
            </div>
        ) : null

        return (
            <Spin spinning={creditCheck.loading || creditCheck.checkingCredit}>
                {creditCheck.automatedCreditPull && (
                    <div className="ssn-tips mb-4">
                        <img src="/images/creditCheckIcon.png" alt="" />
                        <div>We&apos;ll use this info to verify your identity, and checking your credit <span>WON&apos;T</span> affect your score.</div>
                    </div>
                )}
                {creditCheck.showCreditError && (
                    <div className="report-error">
                        <img src="/images/warningSignYellow.png" alt="warning" style={{ width: 28, height: 25, marginRight: 12 }} />
                        <div style={{ whiteSpace: "initial", width: "100%" }}>
                            <div className="report-error-title">
                                <div>Sorry, we were unable to pull your credit report.</div>
                                <div onClick={contactLoanOfficer} style={{ cursor: "pointer" }}>Contact my loan officer</div>
                            </div>
                            <div style={{ fontSize: "16px", color: "#333", lineHeight: "normal" }}>
                                Your SSN: <span>{creditCheck.socialSecurity}</span>
                            </div>
                            {creditCheck.errorMessages.length > 0 && (
                                <div className="creditCheck-fail-msg" style={{ display: "flex" }}>
                                    <span style={{ marginRight: 6 }}>Potential Error Cause:</span>
                                    <div style={{ fontWeight: 500 }}>{creditCheck.errorMessages[0]}</div>
                                </div>
                            )}
                            <div className="creditCheck-fail-operate" style={{ lineHeight: "normal" }}>
                                Continue by entering your estimated FICO score, or Re-entering your SSN to redo the credit check
                            </div>
                        </div>
                    </div>
                )}
                {!creditCheck.automatedCreditPull ? (
                    <div className={creditCheck.showCreditError ? undefined : "pt-3"}>
                        {enterSSN()}
                        {displayEstimatedFicoAndDebt()}
                    </div>
                ) : (
                <div className="credit-info-box">
                    <div className="text-wrap fs-16" style={{ color: "#62707C", lineHeight: "20px", marginBottom: "8px" }}>
                        Please confirm the following info is correct
                    </div>
                    <div className="credit-info">
                        <div className="credit-info-t">
                            <div>
                                <span className="credit-info-lable">Name:</span>
                                <span className="credit-info-value">{name}</span>
                            </div>
                            <div className="credit-info-t-Suffix">
                                <span className="credit-info-lable">Suffix:</span>
                                <span className="credit-info-value">{suffix}</span>
                            </div>
                        </div>
                        <div>
                            <span className="credit-info-lable">Address:</span>
                            <span className="credit-info-value text-wrap">{formatAddress(participant?.presentAddress)}</span>
                        </div>
                    </div>
                    {enterSSN()}
                    {/* Legacy: congratulations only after success while Soft/Manual choice is closed. */}
                    {creditCheck.softPullSucceeded && !creditCheck.showCheckCredit && !creditCheck.showCreditError && (
                        <div className="creditCheck-success">
                            <img src="/images/congratulation2.png" alt="congratulation" />
                            <div className="creditCheck-success-title">Congratulations</div>
                            <div className="creditCheck-success-subtitle">Your soft pull is successful!</div>
                        </div>
                    )}
                    {creditCheck.showCheckCredit && !creditCheck.showFico && (
                        <div className="creditCheck-select mt-4">
                            {creditCheck.ssnChangedAfterPull && (
                                <div
                                    className="text-wrap"
                                    style={{ color: "#62707C", fontWeight: 500, lineHeight: "20px", marginBottom: "26px" }}
                                >
                                    We noticed that you have changed your SSN, Do you want to redo Soft Credit Check, or enter your estimated FICO score ? *** This may affect the relevance of your credit record in our system
                                </div>
                            )}
                            <Form.Group className="inputSection">
                                <Form.Label>
                                    <div className="creditCheck-label">
                                        I would like to check my credit by
                                    </div>
                                </Form.Label>
                                <CheckGroup
                                    data={[
                                        {
                                            value: "true",
                                            label: "Soft credit check",
                                            tag: <div className="creditCheck-recommend">Recommend</div>,
                                        },
                                        {
                                            value: "false",
                                            label: "Enter credit score manually",
                                        },
                                    ]}
                                    onChangeSelect={(value) => setCreditCheck((prev) => ({
                                        ...prev,
                                        pullCredit: value,
                                        softPullSucceeded: value === "true" ? prev.softPullSucceeded : false,
                                        // Legacy: selecting manual keeps radios visible; showFico is fail-path only.
                                        estimatedFico: value === "true" ? 0 : prev.estimatedFico,
                                    }))}
                                    value={creditCheck.pullCredit}
                                    type="radio"
                                    direction="horizontal"
                                />
                            </Form.Group>
                            {creditCheck.pullCredit === "false" && displayEstimatedFicoAndDebt()}
                        </div>
                    )}
                    {/* Legacy fail / post-fail manual entry: FICO without Soft/Manual radios. */}
                    {!creditCheck.showCheckCredit && creditCheck.showFico && displayEstimatedFicoAndDebt()}
                    {creditCheckTips}
                </div>
                )}
                {!creditCheck.automatedCreditPull && creditCheckTips}
                <Modal
                    open={creditCheck.showContactModal}
                    onCancel={() => setCreditField("showContactModal", false)}
                    centered
                    width="auto"
                    footer={[
                        <BootstrapButton key="ok" variant="zeitro-primary" onClick={() => setCreditField("showContactModal", false)}>
                            OK
                        </BootstrapButton>,
                    ]}
                    closable={false}
                >
                    We&apos;ve notified your loan officer about this issue, they will contact you soon.
                </Modal>
            </Spin>
        )
    }

    const renderPropertyMoney = () => (
        <>
            <YesNoQuestion
                fieldKey="occupyasprimary"
                error={fieldErrors.occupyasprimary}
                label="Will you occupy the property as your primary residence?"
                value={declarations.occupyasprimary}
                onChange={(value) => {
                    clearFieldError("occupyasprimary")
                    if (value === "no") clearFieldError("ownershipinterest")
                    setDeclarations((prev) => ({
                        ...prev,
                        occupyasprimary: value,
                        ownershipinterest: value === "no" ? "no" : prev.ownershipinterest,
                    }))
                }}
            />
            {declarations.occupyasprimary === "yes" && (
                <>
                    <YesNoQuestion
                        fieldKey="ownershipinterest"
                        error={fieldErrors.ownershipinterest}
                        label="Have you had an ownership interest in another property in the last three years?"
                        value={declarations.ownershipinterest}
                        onChange={(value) => setField("ownershipinterest", value)}
                    />
                    {declarations.ownershipinterest !== "no" && (
                        <>
                            <Row>
                                <Col className="mt-4 px-0" md={12} xs={12}>
                                    <Form.Group controlId="propertyowned" className="text-left inputSection">
                                        <Form.Label className="d-flex align-items-center flex-wrap">
                                            What type of property did you own?
                                            <Tips
                                                content={<div>
                                                    Select FHA Second Home only if your mortgage is an FHA one, and your property was qualified as FHA secondary for this mortgage
                                                </div>}
                                            />
                                        </Form.Label>
                                        <Row>
                                            <Col className="pl-0" md={6} xs={12}>
                                                <Select
                                                    style={{ width: "100%", height: 44 }}
                                                    value={declarations.propertyowned}
                                                    onChange={(value) => setField("propertyowned", value)}
                                                >
                                                    <option value="principal">Primary Residence</option>
                                                    <option value="secondhome">Second Home</option>
                                                    <option value="fhasecond">FHA Second residence</option>
                                                    <option value="investment">Investment property</option>
                                                </Select>
                                            </Col>
                                        </Row>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col className="mt-4 px-0" md={12} xs={12}>
                                    <Form.Group controlId="titleheld" className="text-left inputSection">
                                        <Form.Label>How did you hold title to the home?</Form.Label>
                                        <Row>
                                            <Col className="pl-0" md={6} xs={12}>
                                                <Select
                                                    style={{ width: "100%", height: 44 }}
                                                    value={declarations.titleheld}
                                                    onChange={(value) => setField("titleheld", value)}
                                                >
                                                    <option value="yourself">By Yourself</option>
                                                    <option value="jointwithspouse">Jointly with your spouse</option>
                                                    <option value="jointwithother">Jointly with Another Person</option>
                                                </Select>
                                            </Col>
                                        </Row>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    )}
                </>
            )}
            {purposeValue(application) === "purchase" && (
                <YesNoQuestion
                    fieldKey="familyrelationship"
                    error={fieldErrors.familyrelationship}
                    label="Do you have a family relationship or business affiliation with the seller of the property?"
                    value={declarations.familyrelationship}
                    onChange={(value) => setField("familyrelationship", value)}
                />
            )}
            <YesNoQuestion
                fieldKey="downpaymentborrowed"
                error={fieldErrors.downpaymentborrowed}
                label={(
                    <>
                        Are you borrowing any money for this real estate transaction?
                        <Tips
                            content={
                                <div>
                                    (e.g., money for your closing costs or down payment) or
                                    obtaining any money from another party, such as the seller or realtor, that you have not disclosed on this loan application?
                                </div>
                            }
                        />
                    </>
                )}
                value={declarations.downpaymentborrowed}
                onChange={(value) => {
                    if (value !== "yes") clearFieldError("moneyborrowed")
                    setField("downpaymentborrowed", value)
                }}
            />
            {declarations.downpaymentborrowed === "yes" && (
                <Row>
                    <Col className="mt-4 px-0" md={12} xs={12} data-field="moneyborrowed">
                        <Form.Group controlId="moneyborrowed" className="m-0 p-0 text-left inputSection">
                            <Form.Label>*What is the amount of this money?</Form.Label>
                            <Row>
                                <Col className="pl-0" md={6} xs={12}>
                                    <DollarInputV2
                                        style={{ width: "100%" }}
                                        value={declarations.moneyborrowed}
                                        status={fieldErrors.moneyborrowed ? "error" : undefined}
                                        onChange={(value) => setField("moneyborrowed", value == null ? "" : `${value}`)}
                                    />
                                    <FieldError message={fieldErrors.moneyborrowed} />
                                </Col>
                            </Row>
                        </Form.Group>
                    </Col>
                </Row>
            )}
            <YesNoQuestion
                fieldKey="applyingonanother"
                error={fieldErrors.applyingonanother}
                label="Have you or will you be applying for a mortgage loan on another property (not the property securing this loan) on or before closing this transaction that is not disclosed on this loan application?"
                value={declarations.applyingonanother}
                onChange={(value) => setField("applyingonanother", value)}
            />
            <YesNoQuestion
                fieldKey="applyingnewcredit"
                error={fieldErrors.applyingnewcredit}
                label={(
                    <>
                        Have you or will you be applying for any new credit on or before closing this loan that is not disclosed on this application?
                        <Tips content={"(E.g., installment loan, credit card, etc.)"} />
                    </>
                )}
                value={declarations.applyingnewcredit}
                onChange={(value) => setField("applyingnewcredit", value)}
            />
            <YesNoQuestion
                fieldKey="cleanenergylien"
                error={fieldErrors.cleanenergylien}
                label={(
                    <>
                        Will this property be subject to a lien that could take priority over the first mortgage lien?
                        <Tips content={"Such as a clean energy lien paid through your property taxes (e.g., the Property Assessed Clean Energy Program)"} />
                    </>
                )}
                value={declarations.cleanenergylien}
                onChange={(value) => setField("cleanenergylien", value)}
            />
        </>
    )

    const renderRadio = ({ name, value, checked, label, onChange, id }) => (
        <div className="eo-option">
            <input type="radio" checked={checked} onChange={onChange} value={value} name={name} id={id} />
            <label className="zeitro-radio text-wrap" htmlFor={id}>{label}</label>
        </div>
    )

    const renderCheck = ({ id, checked, label, onChange, className = "" }) => (
        <div className="eo-option">
            <input type="checkbox" id={id} checked={checked} className={className} onChange={onChange} />
            <label className="zeitro-checkbox text-wrap" htmlFor={id}>{label}</label>
        </div>
    )

    const renderEqualOpportunity = () => {
        const who = participant?.partyId || "participant"
        const { ethnicity, hispanic, race, sex } = declarations.equalopportunity
        const onEthnicity = (event) => setEqualOpportunity("ethnicity", event.target.value)
        const onSex = (event) => setEqualOpportunity("sex", event.target.value)
        const onCheck = (section, key) => (event) => setEqualOpportunitySub(section, key, event.target.checked)

        const otherTextInput = ({ value, onChange, placeholder, error, fieldKey }) => (
            <input
                type="text"
                className="gubmint eo-text-input"
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                data-field={fieldKey}
                style={error ? { borderColor: "#EB3232" } : undefined}
            />
        )

        return (
            <div className="eo-demographics">
                <div className="eo-section" data-field="ethnicity">
                    <Form.Label className="sectionquestion">*Ethnicity</Form.Label>
                    <div className="eo-option-row">
                        {renderRadio({ name: `ethnicity${who}`, value: "hispanic", checked: ethnicity === "hispanic", onChange: onEthnicity, id: `hispanic${who}`, label: "Hispanic or Latino" })}
                        {renderRadio({ name: `ethnicity${who}`, value: "nonhispanic", checked: ethnicity === "nonhispanic", onChange: onEthnicity, id: `nothispanic${who}`, label: "Not Hispanic or Latino" })}
                        {renderRadio({ name: `ethnicity${who}`, value: "donttell", checked: ethnicity === "donttell", onChange: onEthnicity, id: `ethnicitydonttell${who}`, label: "I do not wish to provide this information" })}
                    </div>
                    {ethnicity === "hispanic" && (
                        <div className="eo-subtype" data-field="hispanic">
                            <div className="eo-subtype-hint">Check one or more</div>
                            <div className="eo-option-stack">
                                {renderCheck({ id: `mexican${who}`, checked: hispanic.mexican, label: "Mexican", onChange: onCheck("hispanic", "mexican") })}
                                {renderCheck({ id: `puertorican${who}`, checked: hispanic.puertorican, label: "Puerto Rican", onChange: onCheck("hispanic", "puertorican") })}
                                {renderCheck({ id: `cuban${who}`, checked: hispanic.cuban, label: "Cuban", onChange: onCheck("hispanic", "cuban") })}
                                <div className="eo-option-with-input">
                                    {renderCheck({ id: `otherhispanic${who}`, checked: hispanic.other, label: "Other Hispanic or Latino", onChange: onCheck("hispanic", "other") })}
                                    {hispanic.other && otherTextInput({
                                        value: hispanic.otherdesctiption,
                                        onChange: (event) => setEqualOpportunitySub("hispanic", "otherdesctiption", event.target.value),
                                        placeholder: "Chilean, Perivuan, and so on.",
                                        error: fieldErrors.hispanicOther,
                                        fieldKey: "hispanicOther",
                                    })}
                                </div>
                            </div>
                            {hispanic.other && (
                                <div className="eo-other-error">
                                    <FieldError message={fieldErrors.hispanicOther} />
                                </div>
                            )}
                            <FieldError message={fieldErrors.hispanic} />
                        </div>
                    )}
                    <FieldError message={fieldErrors.ethnicity} />
                </div>

                <div className="eo-section" data-field="sex">
                    <Form.Label className="sectionquestion">*Sex</Form.Label>
                    <div className="eo-option-row">
                        {renderRadio({ name: `sex${who}`, value: "male", checked: sex === "male", onChange: onSex, id: `male${who}`, label: "Male" })}
                        {renderRadio({ name: `sex${who}`, value: "female", checked: sex === "female", onChange: onSex, id: `female${who}`, label: "Female" })}
                        {renderRadio({ name: `sex${who}`, value: "donttell", checked: sex === "donttell", onChange: onSex, id: `sexdonttell${who}`, label: "I do not wish to provide this information" })}
                    </div>
                    <FieldError message={fieldErrors.sex} />
                </div>

                <div className="eo-section" data-field="race">
                    <Form.Label className="sectionquestion">*Race</Form.Label>
                    <div className="eo-option-stack">
                        {renderCheck({ id: `dontwantto${who}`, checked: race.notfurnished, label: "I do not wish to provide this information", onChange: onCheck("race", "notfurnished") })}
                        {!race.notfurnished && (
                            <>
                                {renderCheck({ id: `black${who}`, checked: race.black, label: "Black or African American", onChange: onCheck("race", "black") })}
                                {renderCheck({ id: `white${who}`, checked: race.white, label: "White", onChange: onCheck("race", "white") })}
                                {renderCheck({ id: `pacific${who}`, checked: race.pacific, label: "Native Hawaian or Other Pacific Islander", onChange: onCheck("race", "pacific") })}
                                {race.pacific && (
                                    <div className="eo-subtype" data-field="pacific">
                                        <div className="eo-subtype-hint">Check one or more</div>
                                        <div className="eo-option-stack">
                                            {renderCheck({ id: `hawaian${who}`, checked: race.pacificdetails.hawaian, label: "Native Hawaian", onChange: onCheck("race|pacificdetails", "hawaian") })}
                                            {renderCheck({ id: `guamanian${who}`, checked: race.pacificdetails.guamanian, label: "Guamanian or Chamorro", onChange: onCheck("race|pacificdetails", "guamanian") })}
                                            {renderCheck({ id: `samoan${who}`, checked: race.pacificdetails.samoan, label: "Samoan", onChange: onCheck("race|pacificdetails", "samoan") })}
                                            <div className="eo-option-with-input">
                                                {renderCheck({ id: `otherpacific${who}`, checked: race.pacificdetails.other, label: "Other Pacific Islander", onChange: onCheck("race|pacificdetails", "other") })}
                                                {race.pacificdetails.other && otherTextInput({
                                                    value: race.pacificdetails.otherdetails,
                                                    onChange: (event) => setEqualOpportunitySub("race|pacificdetails", "otherdetails", event.target.value),
                                                    placeholder: "Fijian, Tongan, and so on.",
                                                    error: fieldErrors.pacificOther,
                                                    fieldKey: "pacificOther",
                                                })}
                                            </div>
                                        </div>
                                        {race.pacificdetails.other && (
                                            <div className="eo-other-error">
                                                <FieldError message={fieldErrors.pacificOther} />
                                            </div>
                                        )}
                                        <FieldError message={fieldErrors.pacific} />
                                    </div>
                                )}
                                {renderCheck({ id: `americanindian${who}`, checked: race.americanindian, label: "American Indian or Alaska Native", onChange: onCheck("race", "americanindian") })}
                                {race.americanindian && (
                                    <div className="eo-subtype" data-field="indiantribe">
                                        <div className="eo-option-with-input eo-tribe-row">
                                            <span className="eo-tribe-label">*Print name of enrolled or principal tribe:</span>
                                            {otherTextInput({
                                                value: race.indiantribe,
                                                onChange: (event) => setEqualOpportunitySub("race", "indiantribe", event.target.value),
                                                placeholder: "",
                                                error: fieldErrors.indiantribe,
                                            })}
                                        </div>
                                        <div className="eo-other-error">
                                            <FieldError message={fieldErrors.indiantribe} />
                                        </div>
                                    </div>
                                )}
                                {renderCheck({ id: `asian${who}`, checked: race.asian, label: "Asian", onChange: onCheck("race", "asian") })}
                                {race.asian && (
                                    <div className="eo-subtype" data-field="asian">
                                        <div className="eo-subtype-hint">Check one or more</div>
                                        <div className="eo-option-row">
                                            {renderCheck({ id: `asianindian${who}`, checked: race.asiandetails.asianindian, label: "Asian Indian", onChange: onCheck("race|asiandetails", "asianindian") })}
                                            {renderCheck({ id: `chinese${who}`, checked: race.asiandetails.chinese, label: "Chinese", onChange: onCheck("race|asiandetails", "chinese") })}
                                            {renderCheck({ id: `filipino${who}`, checked: race.asiandetails.filipino, label: "Filipino", onChange: onCheck("race|asiandetails", "filipino") })}
                                        </div>
                                        <div className="eo-option-row">
                                            {renderCheck({ id: `japanese${who}`, checked: race.asiandetails.japanese, label: "Japanese", onChange: onCheck("race|asiandetails", "japanese") })}
                                            {renderCheck({ id: `korean${who}`, checked: race.asiandetails.korean, label: "Korean", onChange: onCheck("race|asiandetails", "korean") })}
                                            {renderCheck({ id: `vietnamese${who}`, checked: race.asiandetails.vietnamese, label: "Vietnamese", onChange: onCheck("race|asiandetails", "vietnamese") })}
                                        </div>
                                        <div className="eo-option-with-input">
                                            {renderCheck({ id: `otherasian${who}`, checked: race.asiandetails.otherasian, label: "Other Asian", onChange: onCheck("race|asiandetails", "otherasian") })}
                                            {race.asiandetails.otherasian && otherTextInput({
                                                value: race.asiandetails.otherdesctiption,
                                                onChange: (event) => setEqualOpportunitySub("race|asiandetails", "otherdesctiption", event.target.value),
                                                placeholder: "Hmong, Laotian, and so on.",
                                                error: fieldErrors.asianOther,
                                                fieldKey: "asianOther",
                                            })}
                                        </div>
                                        {race.asiandetails.otherasian && (
                                            <div className="eo-other-error">
                                                <FieldError message={fieldErrors.asianOther} />
                                            </div>
                                        )}
                                        <FieldError message={fieldErrors.asian} />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <FieldError message={fieldErrors.race} />
                </div>
            </div>
        )
    }

    const renderFinances = () => (
        <>
            <YesNoQuestion label="Are you a co-signer or guarantor on any debt or loan that is not disclosed on this application?" value={declarations.comakeronnote} onChange={(value) => setField("comakeronnote", value)} />
            <YesNoQuestion label="Are there any outstanding judgments against you?" value={declarations.judgements} onChange={(value) => setField("judgements", value)} />
            <YesNoQuestion label="Are you currently delinquent or in default on a Federal debt?" value={declarations.delinquentondebt} onChange={(value) => setField("delinquentondebt", value)} />
            <YesNoQuestion label="Are you a party to a lawsuit in which you potentially have any personal financial liability?" value={declarations.lawsuit} onChange={(value) => setField("lawsuit", value)} />
            <YesNoQuestion label="Have you conveyed title to any property in lieu of foreclosure in the past 7 years?" value={declarations.conveyedtitle} onChange={(value) => setField("conveyedtitle", value)} />
            <YesNoQuestion label="Within the past 7 years, have you completed a pre-foreclosure sale or short sale? whereby the property was sold to a third party and the Lender agreed to accept less than the outstanding mortgage balance due?" value={declarations.loanforeclosure} onChange={(value) => setField("loanforeclosure", value)} />
            <YesNoQuestion label="Have you had property foreclosed upon in the last 7 years?" value={declarations.propertyforeclosure} onChange={(value) => setField("propertyforeclosure", value)} />
            <YesNoQuestion label="Do you currently have any loans in forebearance?" value={declarations.loansinforebearance} onChange={(value) => setField("loansinforebearance", value)} />
            <YesNoQuestion label="Have you been declared bankrupt within the past 7 years?" value={declarations.bancrupt} onChange={(value) => setField("bancrupt", value)} />
            {declarations.bancrupt === "yes" && (
                <>
                    <div style={{ marginTop: "10px", color: "#222222", fontSize: 16 }}>
                        please Identify the type(s) of bankruptcy:
                    </div>
                    <Row>
                        <Col md="auto" style={{ display: "flex", alignItems: "center", marginTop: "10px" }}>
                            <Checkbox checked={declarations.chapter7} onChange={(e) => setField("chapter7", e.target.checked)}>chapter7</Checkbox>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="auto" style={{ display: "flex", alignItems: "center", marginTop: "10px" }}>
                            <Checkbox checked={declarations.chapter11} onChange={(e) => setField("chapter11", e.target.checked)}>chapter11</Checkbox>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="auto" style={{ display: "flex", alignItems: "center", marginTop: "10px" }}>
                            <Checkbox checked={declarations.chapter12} onChange={(e) => setField("chapter12", e.target.checked)}>chapter12</Checkbox>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="auto" style={{ display: "flex", alignItems: "center", marginTop: "10px" }}>
                            <Checkbox checked={declarations.chapter13} onChange={(e) => setField("chapter13", e.target.checked)}>chapter13</Checkbox>
                        </Col>
                    </Row>
                </>
            )}
            <YesNoQuestion
                label="Did you (or your deceased spouse) ever serve, or are you currently serving, in the United States Armed Forces?"
                value={declarations.servearmedforces}
                onChange={(value) => setField("servearmedforces", value)}
            />
            {declarations.servearmedforces === "yes" && (
                <div className="military-service-options">
                    <div className="military-service-item">
                        <Checkbox
                            className="finance-checkbox"
                            checked={declarations.militaryservice.currentlyserving === true}
                            onChange={(e) => setMilitary("currentlyserving", e.target.checked)}
                        >
                            Currently serving on active duty with projected expiration date of service/tour
                        </Checkbox>
                        {declarations.militaryservice.currentlyserving && (
                            <div className="military-expiration">
                                <div className="military-expiration-label">Expiration date</div>
                                <DatePicker
                                    className="military-expiration-picker"
                                    placeholder="MM/DD/YYYY"
                                    format="MM/DD/YYYY"
                                    value={declarations.militaryservice.projectedexpirationdate ? dayjs(declarations.militaryservice.projectedexpirationdate) : null}
                                    onChange={(date) => setMilitary("projectedexpirationdate", date ? date.format("MM/DD/YYYY") : "")}
                                />
                            </div>
                        )}
                    </div>
                    <div className="military-service-item">
                        <Checkbox
                            className="finance-checkbox"
                            checked={declarations.militaryservice.currentlyretired === true}
                            onChange={(e) => setMilitary("currentlyretired", e.target.checked)}
                        >
                            Currently retired, discharged, or separated from service
                        </Checkbox>
                    </div>
                    <div className="military-service-item">
                        <Checkbox
                            className="finance-checkbox"
                            checked={declarations.militaryservice.nonactive === true}
                            onChange={(e) => setMilitary("nonactive", e.target.checked)}
                        >
                            Only period of service was as a non-activated member of the Reserve or National Guard
                        </Checkbox>
                    </div>
                    <div className="military-service-item">
                        <Checkbox
                            className="finance-checkbox"
                            checked={declarations.militaryservice.survivingspouse === true}
                            onChange={(e) => setMilitary("survivingspouse", e.target.checked)}
                        >
                            Surviving spouse
                        </Checkbox>
                    </div>
                </div>
            )}
            {renderEqualOpportunity()}
        </>
    )

    return (
        <>
            <div className="text-left application-v2-form-body">
                <div className="page-header page-header-row">
                    <span className="page-header-primary">Declarations</span>
                    <span className="page-header-separator">/</span>
                    <span className="page-header-secondary">{STEP_TITLE[step] || STEP_TITLE.propertymoney}</span>
                </div>
                <Form ref={formRef} noValidate>
                    {step === "finances" ? renderFinances() : step === "creditcheck" ? renderCreditCheck() : renderPropertyMoney()}
                </Form>
            </div>
            <Row className="text-left application-bottom-bar">
                <div className="d-flex align-items-center">
                    <BootstrapButton type="button" variant="zeitro-primary" disabled={saving} className="operate-btn back mr-3" onClick={onBack}>
                        Back
                    </BootstrapButton>
                    <BootstrapButton
                        type="button"
                        variant="zeitro-primary"
                        disabled={saving || creditCheck.checkingCredit}
                        className="text-center operate-btn btn"
                        onClick={() => {
                            if (step === "propertymoney") {
                                const { errors, firstErrorKey } = validatePropertyMoney(declarations)
                                if (firstErrorKey) {
                                    setFieldErrors(errors)
                                    setTimeout(() => scrollToField(firstErrorKey), 50)
                                    return
                                }
                                setFieldErrors({})
                            } else if (step === "finances") {
                                const { errors, firstErrorKey } = validateFinances(declarations)
                                if (firstErrorKey) {
                                    setFieldErrors(errors)
                                    setTimeout(() => scrollToField(firstErrorKey), 50)
                                    return
                                }
                                setFieldErrors({})
                            } else if (step === "creditcheck") {
                                const { errors, firstErrorKey } = validateCreditCheck(creditCheck)
                                if (firstErrorKey) {
                                    setFieldErrors(errors)
                                    setTimeout(() => scrollToField(firstErrorKey), 50)
                                    return
                                }
                                setFieldErrors({})
                            }
                            const needsSoftPull = step === "creditcheck"
                                && creditCheck.automatedCreditPull
                                && creditCheck.pullCredit === "true"
                                && creditCheck.showCheckCredit
                                && !creditCheck.showFico
                            if (needsSoftPull) {
                                requestSoftCredit()
                                return
                            }
                            const creditPayload = withEstimatedLiabilityIfManual(creditCheck, liabilities, participant)
                            const payload = {
                                step,
                                declarations,
                                creditCheck: {
                                    ...creditPayload,
                                    creditWarnings: null,
                                    complete: true,
                                },
                            }
                            if (step === "creditcheck" && canSubmitApplication) {
                                setPendingSubmit(payload)
                                setShowSubmitModal(true)
                                return
                            }
                            onSave(payload)
                        }}
                    >
                        {step === "creditcheck"
                            && creditCheck.automatedCreditPull
                            && creditCheck.pullCredit === "true"
                            && creditCheck.showCheckCredit
                            && !creditCheck.showFico
                            ? "Check Credit"
                            : canSubmitApplication
                                ? (creditCheck.automatedCreditPull ? "Authorize & submit" : "Submit")
                                : "Next"}
                    </BootstrapButton>
                </div>
            </Row>
            <Modal
                title={!isMobileSubmit && "Congratulations on getting ready to submit your loan application!"}
                open={showSubmitModal}
                onOk={confirmSubmitApplication}
                onCancel={() => {
                    setShowSubmitModal(false)
                    setPendingSubmit(null)
                }}
                okText="Submit"
                cancelText="Cancel"
                confirmLoading={saving}
                centered
                className={isMobileSubmit ? "application-interview-submit-modal" : ""}
                footer={isMobileSubmit ? [
                    <div key="footer" className="mobile-modal-footer">
                        <AntdButton
                            className="mobile-modal-footer-button-cancel"
                            type="link"
                            onClick={() => {
                                setShowSubmitModal(false)
                                setPendingSubmit(null)
                            }}
                        >
                            Cancel
                        </AntdButton>
                        <AntdButton
                            className="mobile-modal-footer-button-submit"
                            type="primary"
                            onClick={confirmSubmitApplication}
                            loading={saving}
                        >
                            Submit
                        </AntdButton>
                    </div>,
                ] : undefined}
            >
                {isMobileSubmit ? (
                    <>
                        <div className="mobile-modal-title">Submit your application?</div>
                        <div className="mobile-modal-content">
                            Once you submit, your loan officer will review it and reach out with next steps.
                        </div>
                    </>
                ) : (
                    <>
                        <p>What's the next step?</p>
                        <p>
                            Your loan officer will review your application and keep you updated on any progress or any further tasks. Once your submission is complete, feel free to:{' '}
                        </p>
                        <ul>
                            <li>Check out your personalized rates by selecting "Continue"</li>
                            <li>Fill out declarations</li>
                            <li>Move ahead with uploading the required documents </li>
                        </ul>
                        <p>
                            Don't hesitate to reach out if you have any questions or concerns!
                        </p>
                    </>
                )}
            </Modal>
        </>
    )
}

export default DeclarationStepV2
